import {createSlice} from "@reduxjs/toolkit";
import AppConfig from "../../helpers/config";

const initialState = {
    selectedHotel: null,
    selectedHotelToShare: null,
    selectedHotelDetail: [],
    paginatedRoomTypes: [],
    roomFlights: [],
    paginatedRoomFlights: [],
    defaultPrice: {
        low: AppConfig.minPrice,
        high: AppConfig.maxPrice,
    },
    // The chat's price slider, remembered per product so a Package band never
    // carries over to Stay only (or back). null = not set yet for that product.
    chatPriceByProduct: {
        package: null,
        stayOnly: null,
    },
    travelDate: {
        startDate: "",
        endDate: "",
    },
    travelTime: null,
    travelers: [
        {
            child: 0,
            adult: 2,
            infants: 0,
        },
    ],
    departureFrom: [],
    selectedDesireCountry: null,
    appFilters: [
        {
            _id: "checkbox-0-Direct-steps",
            id: 0,
            key: "steps",
            text: "Direct",
        },
        {
            _id: "checkbox-1-ECONOMY-airline_class",
            id: 1,
            key: "airline_class",
            text: "ECONOMY",
            value: "ECONOMY",
        },
        {
            _id: "checkbox-1-Anytime-arrival_time",
            id: 1,
            key: "arrival_time",
            text: "Anytime"
        },
        {
            _id: "checkbox-1-Anytime-departure_time",
            id: 1,
            key: "departure_time",
            text: "Anytime"
        }
    ],
    listingGuid: null,
    numberOfStops: 0,
    starRating: {min: AppConfig.minStarRating, max: AppConfig.minStarRating},
    flightCabinClass: "ECONOMY",
    selectedFavoriteHotel: null,
    gptFilters: {
        to: '',
        departure: '',
        travelers: '',
        infants: '',
        leaveTime: '',
        nightCount: 0,
        priceSpend: {
            low: AppConfig.minPrice,
            high: AppConfig.maxPrice,
        },
        initialChat: true,
    },
};

export const searchFilterSlice = createSlice({
    name: "searchfilters",
    initialState,
    reducers: {
        resetSearchFiltersState: () => initialState,
        setSelectedHotel: (state, action) => {
            let payload = action.payload;
            if (state.selectedHotel !== null && payload) {
                payload = {...state.selectedHotel, ...payload};
            }
            state.selectedHotel = payload;
        },
        setSelectedHotelToShare: (state, action) => {
            state.selectedHotelToShare = action.payload;
        },
        setSelectedHotelDetail: (state, action) => {
            state.selectedHotelDetail = action.payload;
        },
        setTravelDate: (state, action) => {
            state.travelDate = action.payload;
        },
        setTravelers: (state, action) => {
            state.travelers = action.payload;
        },
        setDepartureFrom: (state, action) => {
            state.departureFrom = action.payload;
        },
        setPriceRangeValue: (state, action) => {
            state.defaultPrice = action.payload;
        },
        setChatProductPrice: (state, action) => {
            const {stayOnly, band} = action.payload;
            state.chatPriceByProduct = {...state.chatPriceByProduct, [stayOnly ? 'stayOnly' : 'package']: band};
        },
        setRoomFlights: (state, action) => {
            state.roomFlights = action.payload;
        },
        setFlightCabinClass: (state, action) => {
            state.flightCabinClass = action.payload;
        },
        setDesireCountry: (state, action) => {
            state.selectedDesireCountry = action.payload;
        },
        setTravelTime: (state, action) => {
            state.travelTime = action.payload;
        },
        setAppFilters: (state, action) => {
            state.appFilters = action.payload;
        },
        setListingGuid: (state, action) => {
            state.listingGuid = action.payload;
        },
        setNumberOfStops: (state, action) => {
            state.numberOfStops = action.payload;
        },
        setStarRating: (state, action) => {
            state.starRating = action.payload;
        },
        setPaginatedRoomTypes: (state, action) => {
            state.paginatedRoomTypes = action.payload;
        },
        setPaginatedRoomFlights: (state, action) => {
            state.paginatedRoomFlights = action.payload;
        },
        setSelectedFavoriteHotel: (state, action) => {
            state.selectedFavoriteHotel = action.payload;
        },
        setResetSlice: state => {
            const _GPTFilter = state.gptFilters;
            Object.keys(initialState).forEach(f => {
                state[f] = initialState[f];
            });
            state.gptFilters = _GPTFilter;
        },
        setGPTTo: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, to: action.payload};
        },
        setGPTDeparture: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, departure: action.payload};
        },
        setGPTTravelers: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, travelers: action.payload};
        },
        setGPTInfants: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, infants: action.payload};
        },
        setGPTLeaveTime: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, leaveTime: action.payload};
        },
        setGPTNightCount: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, nightCount: action.payload};
        },
        setGPTPriceRange: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, priceSpend: action.payload};
        },
        setInitialChat: (state, action) => {
            const prevState = {...state.gptFilters};
            state.gptFilters = {...prevState, initialChat: action.payload};
        },
        clearGPTLocationFilters: (state) => {
            state.gptFilters.to = '';
            state.gptFilters.departure = '';
        },
    },
});

export const getSearchFilters = (state) => state.searchfilters;

// Action creators are generated for each case reducer function
export const {
    resetSearchFiltersState,
    setSelectedPackage,
    setTravelDate,
    setTravelers,
    setDepartureFrom,
    setPriceRangeValue,
    setChatProductPrice,
    setSelectedHotel,
    setSelectedHotelToShare,
    setSelectedHotelDetail,
    setRoomFlights,
    setFlightCabinClass,
    setDesireCountry,
    setTravelTime,
    setAppFilters,
    setListingGuid,
    setNumberOfStops,
    setStarRating,
    setPaginatedRoomTypes,
    setPaginatedRoomFlights,
    setSelectedFavoriteHotel,
    setResetSlice,
    setGPTTo,
    setGPTDeparture,
    setGPTTravelers,
    setGPTInfants,
    setInitialChat,
    setGPTLeaveTime,
    setGPTNightCount,
    setGPTPriceRange,
    clearGPTLocationFilters,
} = searchFilterSlice.actions;

export default searchFilterSlice.reducer;
